import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Header from "./Header";
import { FaArrowUp, FaBars, FaTimes, FaYoutube } from "react-icons/fa";
import "@fortawesome/fontawesome-free/css/all.min.css";
import CrackersLoader from "./CrackersLoader";
import "./quick.css";

// Helper function to round to nearest whole number
const roundToNearestRupee = (amount) => {
  return Math.round(amount);
};

const Quick = () => {
  const [categories, setCategories] = useState([]);
  const [storeClose, setStoreClose] = useState(0);
  const [counts, setCounts] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 426);
  const [searchQuery, setSearchQuery] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [discountvalue, setDiscountvalue] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [discountTotal, setDiscountTotal] = useState();
  const [nonDiscountTotal, setNonDiscountTotal] = useState();
  const qtyCount = Object.values(counts).reduce((acc, count) => acc + count, 0);
  const [showButton, setShowButton] = useState(false);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [showCategorySidebar, setShowCategorySidebar] = useState(false);
  const location = useLocation();
  const selectedCategory = location.state?.selectedCategory;
  const [selectedVideo, setSelectedVideo] = useState(null);
  const sectionRefs = useRef({});
  const headerRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const API_ADMIN_BASE_URL = process.env.REACT_APP_ADMIN_API_BASE_URL;

  // State for settings data
  const [settingsData, setSettingsData] = useState({
    minimum_order_amount: "",
    other_state_minimum_order: ""
  });

  // Discount calculations
  const discountRate = Math.round(discountvalue);
  const discountAmount = (discountTotal * discountRate) / 100;
  const finalAmount = discountTotal - discountAmount + nonDiscountTotal;

  // Handle sticky header - appears only after scrolling past the main header
  useEffect(() => {
    const handleScroll = () => {
      const mainHeader = document.querySelector('.main-header-white-bg');
      const mainHeaderHeight = mainHeader ? mainHeader.offsetHeight : 80;
      const minOrderBanner = document.querySelector('.minimum-order');
      const bannerHeight = minOrderBanner ? minOrderBanner.offsetHeight : 40;
      const scrollThreshold = mainHeaderHeight + bannerHeight;

      if (window.pageYOffset > scrollThreshold) {
        setIsHeaderFixed(true);
      } else {
        setIsHeaderFixed(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isSmallScreen) {
      const style = document.createElement('style');
      style.textContent = `
      @media (max-width: 426px) {
        .product-table table {
          width: 100% !important;
          table-layout: fixed !important;
        }
        .category-header td {
          text-align: center !important;
          justify-content: center !important;
        }
        .ctgyname {
          justify-content: center !important;
          text-align: center !important;
          width: 100% !important;
        }
      }
    `;
      document.head.appendChild(style);
      return () => document.head.removeChild(style);
    }
  }, [isSmallScreen]);

  useEffect(() => {
    if (!selectedCategory) return;

    const scrollToCategory = () => {
      const section = sectionRefs.current[selectedCategory];
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        setTimeout(scrollToCategory, 100);
      }
    };

    scrollToCategory();
  }, [selectedCategory]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const count = parseInt(localStorage.getItem("cartCount")) || 0;
    setCartCount(count);

    const handleStorageChange = () => {
      const count = parseInt(localStorage.getItem("cartCount")) || 0;
      setCartCount(count);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const fetchCartItems = () => {
    const items = JSON.parse(localStorage.getItem("cartItems")) || [];
    setCartItems(items);
  };

  // Fetch settings data
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_ADMIN_BASE_URL}setting_detail.php`);
      if (response.data && response.data.body) {
        setSettingsData({
          minimum_order_amount: response.data.body.minimum_order_amount || "",
          other_state_minimum_order: response.data.body.other_state_minimum_order || ""
        });
        // Also update store_close if available in settings
        if (response.data.body.store_close !== undefined) {
          setStoreClose(Number(response.data.body.store_close));
        }
        return response.data;
      }
    } catch (error) {
      console.error("There was an error fetching settings!", error);
    }
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both settings and products concurrently
        const [settingsResponse, productsResponse] = await Promise.all([
          axios.get(`${API_ADMIN_BASE_URL}setting_detail.php`).catch(err => {
            console.error("Settings API error:", err);
            return null;
          }),
          axios.post(`${API_BASE_URL}quick_buy.php`).catch(err => {
            console.error("Products API error:", err);
            return null;
          })
        ]);

        // Process settings if available
        if (settingsResponse && settingsResponse.data && settingsResponse.data.body) {
          setSettingsData({
            minimum_order_amount: settingsResponse.data.body.minimum_order_amount || "",
            other_state_minimum_order: settingsResponse.data.body.other_state_minimum_order || ""
          });
          if (settingsResponse.data.body.store_close !== undefined) {
            setStoreClose(Number(settingsResponse.data.body.store_close));
          }
        }

        // Process products if available
        if (productsResponse && productsResponse.data && productsResponse.data.body) {
          const { body } = productsResponse.data;
          const initialCounts = body.category
            .flatMap((category) =>
              category.product.map((product) => ({
                product_id: product.product_id,
                count:
                  parseInt(localStorage.getItem(`count_${product.product_id}`)) ||
                  0,
              }))
            )
            .reduce((acc, { product_id, count }) => {
              acc[product_id] = count;
              return acc;
            }, {});

          setCategories(body.category || []);
          setDiscountvalue(body.discount_value[0]?.discount_value || []);
          // Only set store_close from products if not already set from settings
          if (body.store_close !== undefined && storeClose === 0) {
            setStoreClose(Number(body.store_close));
          }
          setCounts(initialCounts);
          fetchCartItems();
        } else {
          // Fallback: try to fetch products alone
          try {
            const productsResponseFallback = await axios.post(`${API_BASE_URL}quick_buy.php`);
            const { body } = productsResponseFallback.data;
            const initialCounts = body.category
              .flatMap((category) =>
                category.product.map((product) => ({
                  product_id: product.product_id,
                  count:
                    parseInt(localStorage.getItem(`count_${product.product_id}`)) ||
                    0,
                }))
              )
              .reduce((acc, { product_id, count }) => {
                acc[product_id] = count;
                return acc;
              }, {});

            setCategories(body.category || []);
            setDiscountvalue(body.discount_value[0]?.discount_value || []);
            setStoreClose(Number(body.store_close));
            setCounts(initialCounts);
            fetchCartItems();
          } catch (productError) {
            console.error("Error fetching products:", productError);
          }
        }
      } catch (error) {
        console.error("There was an error fetching the data!", error);
        // Try to fetch products separately as last resort
        try {
          const productsResponse = await axios.post(`${API_BASE_URL}quick_buy.php`);
          const { body } = productsResponse.data;
          const initialCounts = body.category
            .flatMap((category) =>
              category.product.map((product) => ({
                product_id: product.product_id,
                count:
                  parseInt(localStorage.getItem(`count_${product.product_id}`)) ||
                  0,
              }))
            )
            .reduce((acc, { product_id, count }) => {
              acc[product_id] = count;
              return acc;
            }, {});

          setCategories(body.category || []);
          setDiscountvalue(body.discount_value[0]?.discount_value || []);
          setStoreClose(Number(body.store_close));
          setCounts(initialCounts);
          fetchCartItems();
        } catch (productError) {
          console.error("Error fetching products:", productError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 426);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      fetchCartItems();
    };

    window.addEventListener("storage", handleStorageChange);
    const handleCartUpdated = () => {
      fetchCartItems();
    };
    window.addEventListener("cartUpdated", handleCartUpdated);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cartUpdated", handleCartUpdated);
    };
  }, []);

  const updateTotals = useCallback(
    (counts) => {
      let discountTotal = 0;
      let nonDiscountTotal = 0;

      categories.forEach((category) => {
        category.product.forEach((product) => {
          const count = counts[product.product_id] || 0;
          const price = parseFloat(product.oldprice.replace(/[^\d.-]/g, ""));

          if (!isNaN(price)) {
            if (category.non_discount === "0") {
              discountTotal += count * price;
            } else {
              nonDiscountTotal += count * price;
            }
          }
        });
      });

      setTotalAmount(discountTotal + nonDiscountTotal);
      setDiscountTotal(discountTotal);
      setNonDiscountTotal(nonDiscountTotal);
    },
    [categories]
  );

  useEffect(() => {
    updateTotals(counts);
  }, [counts, updateTotals]);

  // Handle manual input change for count
  const handleCountChange = (product_id, value) => {
    if (value === '') {
      const newCounts = {
        ...counts,
        [product_id]: 0,
      };
      setCounts(newCounts);
      saveCountsToLocalStorage(newCounts);
      return;
    }

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      const newCounts = {
        ...counts,
        [product_id]: numValue,
      };
      setCounts(newCounts);
      saveCountsToLocalStorage(newCounts);
    }
  };

  const handleCountBlur = (product_id, value) => {
    if (value === '' || isNaN(parseInt(value, 10))) {
      const newCounts = {
        ...counts,
        [product_id]: 0,
      };
      setCounts(newCounts);
      saveCountsToLocalStorage(newCounts);
    }
  };

  const handleIncrement = (product_id, product) => {
    const newCounts = {
      ...counts,
      [product_id]: (counts[product_id] || 0) + 1,
    };
    setCounts(newCounts);
    saveCountsToLocalStorage(newCounts);
  };

  const handleDecrement = (product_id) => {
    const currentCount = counts[product_id] || 0;
    if (currentCount > 0) {
      const newCounts = {
        ...counts,
        [product_id]: currentCount - 1,
      };
      setCounts(newCounts);
      saveCountsToLocalStorage(newCounts);
    }
  };

  const saveCountsToLocalStorage = (counts) => {
    Object.entries(counts).forEach(([product_id, count]) => {
      localStorage.setItem(`count_${product_id}`, count);
    });

    const addedItems = categories
      .flatMap((category) =>
        category.product.map((product) => ({
          ...product,
          count: counts[product.product_id] || 0,
          category_id: category.category_id,
          is_non_discount: product.is_non_discount,
        }))
      )
      .filter((item) => item.count > 0);

    localStorage.setItem("cartItems", JSON.stringify(addedItems));

    const totalUniqueProductCount = Object.keys(counts).filter(
      (key) => counts[key] > 0
    ).length;
    localStorage.setItem("cartCount", totalUniqueProductCount);

    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const filteredCategories = categories
    .map((category) => {
      const filteredProducts = category.product.filter((product) =>
        product.product_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return {
        ...category,
        product: filteredProducts,
      };
    })
    .filter((category) => category.product.length > 0);

  const handleImageClick = (imageSrc, youtubeUrl, name) => {
    let embedUrl = null;

    if (youtubeUrl) {
      const videoIdMatch = youtubeUrl.match(/(?:shorts\/|watch\?v=)([a-zA-Z0-9_-]+)/);
      if (videoIdMatch && videoIdMatch[1]) {
        embedUrl = `https://www.youtube.com/embed/${videoIdMatch[1]}`;
      }
    }

    setSelectedImage(imageSrc);
    setSelectedName(name);
    setSelectedVideo(embedUrl);
    setShowModal(true);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleVideoIconClick = (youtubeUrl, productName, imageSrc) => {
    let embedUrl = null;
    if (youtubeUrl) {
      const videoIdMatch = youtubeUrl.match(/(?:shorts\/|watch\?v=)([a-zA-Z0-9_-]+)/);
      if (videoIdMatch && videoIdMatch[1]) {
        embedUrl = `https://www.youtube.com/embed/${videoIdMatch[1]}`;
      }
    }
    setSelectedImage(imageSrc);
    setSelectedName(productName);
    setSelectedVideo(embedUrl);
    setShowModal(true);
  };

  // Toggle Category Sidebar
  const toggleCategorySidebar = () => {
    setShowCategorySidebar(!showCategorySidebar);
  };

  const closeCategorySidebar = () => {
    setShowCategorySidebar(false);
  };

  const scrollToCategory = (categoryName) => {
    setShowCategorySidebar(false);
    const section = sectionRefs.current[categoryName];
    if (section) {
      const floatingHeader = document.querySelector('.floating-header');
      const headerHeight = floatingHeader ? floatingHeader.offsetHeight : 80;
      const sectionRect = section.getBoundingClientRect();
      const absoluteSectionTop = sectionRect.top + window.pageYOffset;
      const offset = headerHeight + 15;
      const scrollPosition = absoluteSectionTop - offset;
      window.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      localStorage.setItem("cartCount", cartItems.length);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [cartItems]);

  if (isLoading) {
    return <CrackersLoader />;
  }

  return (
    <div className="main quick-bottom-gap">
      <Header />

      {/* Dynamic Minimum Order Message */}
      <h5 className="minimum-order">
        Minimum Order Tamilnadu Rs:{parseFloat(settingsData.minimum_order_amount).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} 
        {" "}and Other States Rs:{parseFloat(settingsData.other_state_minimum_order).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      </h5>

      {/* ===== FLOATING HEADER - Search + Menu + Totals ===== */}
      <div ref={headerRef} className={`floating-header ${isHeaderFixed ? 'fixed' : ''}`}>
        <div className="floating-header-content">
          {/* Menu Button */}
          <button
            className="category-toggle-btn"
            onClick={toggleCategorySidebar}
            aria-label="Toggle Categories"
          >
            <FaBars />
            <span className="menu-text">Menu</span>
          </button>

          {/* Search Bar */}
          <div className="floating-search">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              className="floating-search-input"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
            />
            {searchQuery && (
              <button className="floating-search-clear" onClick={clearSearch}>
                <i className="fas fa-times-circle"></i>
              </button>
            )}
          </div>

          {/* Totals Summary */}
          <div className="floating-summary">
            <div className="quick-summary-item">
              <span className="summary-label">Discount: </span>
              <span className="summary-value discount"> -₹{roundToNearestRupee(discountAmount)}</span>
            </div>
            <div className="quick-summary-item">
              <span className="summary-label">Qty: </span>
              <span className="summary-value qty">{qtyCount}</span>
            </div>
            <div className="quick-summary-item">
              <span className="summary-label">Total: </span>
              <span className="summary-value final">₹{roundToNearestRupee(finalAmount)}</span>
            </div>
          </div>

          {/* Cart Icon */}
          <div className="floating-cart">
            <Link to="/cart">
              <i className="bi bi-cart"></i>
              {cartCount > 0 && <span className="cart-badge-floating">{cartCount}</span>}
            </Link>
          </div>
        </div>
      </div>

      {/* Category Sidebar */}
      {showCategorySidebar && (
        <>
          <div className="category-sidebar-overlay" onClick={closeCategorySidebar}></div>
          <div className="category-sidebar">
            <div className="category-sidebar-header">
              <h5>Categories</h5>
              <button className="close-category-btn" onClick={closeCategorySidebar}>
                <FaTimes />
              </button>
            </div>
            <div className="category-sidebar-body">
              {categories.map((category) => (
                <div
                  key={category.category_id}
                  className="category-sidebar-item"
                  onClick={() => scrollToCategory(category.category_name)}
                >
                  <span className="category-sidebar-icon">
                    <i className="bi bi-tag"></i>
                  </span>
                  <span className="category-sidebar-name">{category.category_name}</span>
                  <span className="category-sidebar-count">
                    {category.product.length}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Spacer for fixed header */}
      <div className={`header-spacer ${isHeaderFixed ? 'visible' : ''}`}></div>

      {/* ===== PRODUCT TABLE - KEPT EXACTLY AS IS ===== */}
      <div className="product-table">
        <Table striped bordered hover>
          <thead style={{ "--bs-table-bg": "#c80b09", "--bs-table-color": "white", "color": "white" }}>
            <tr>
              <th className="image-column">Image</th>
              <th className="name-column">Product Name</th>
              <th className="name-column-small-scrn">Product Particulars</th>
              {!isSmallScreen && <th className="qty-per-box-column">Qty per Box</th>}
              {!isSmallScreen && <th className="price-column">Price</th>}
              {!isSmallScreen && storeClose === 0 && <th className="qty-column">Qty</th>}
              {!isSmallScreen && storeClose === 0 && <th className="total-column">Total</th>}
            </tr>
          </thead>
          <tbody>
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category, categoryIndex) => (
                <React.Fragment key={category.category_id}>
                  <tr className="category-header">
                    <td colSpan={isSmallScreen ? 4 : 7} className="category-header">
                      <h5
                        className="ctgyname"
                        ref={(el) => {
                          sectionRefs.current[category.category_name] = el;
                        }}
                      >
                        {category.category_name}
                      </h5>
                    </td>
                  </tr>
                  {category.product.map((product, productIndex) => {
                    const roundedOldPrice = product.oldprice ? roundToNearestRupee(parseFloat(product.oldprice.replace(/[^\d.-]/g, ""))) : 0;
                    const roundedPrice = product.price ? roundToNearestRupee(parseFloat(product.price.replace(/[^\d.-]/g, ""))) : 0;

                    return (
                      <tr key={product.product_id}>
                        <td className="tab-img image-column">
                          {product.image && (
                            <div style={{ position: "relative", display: "inline-block" }}>
                              <img
                                className="product-img-quick"
                                src={product.image}
                                alt={product.product_name}
                                style={{
                                  width: "70px",
                                  height: "70px",
                                  objectFit: "contain",
                                  cursor: "pointer"
                                }}
                                loading="lazy"
                                onClick={() =>
                                  handleImageClick(product.image, product.youtube_url, product.product_name)
                                }
                              />
                            </div>
                          )}
                        </td>
                        <td className="product-detail-column name-column">
                          <div className="product-detail">
                            <strong className="productname">
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <span>
                                  {product.product_name}
                                </span>
                                {!isSmallScreen && product.youtube_url && (
                                  <span
                                    className="youtube-icon-title"
                                    onClick={() => handleVideoIconClick(product.youtube_url, product.product_name, product.image)}
                                    style={{ cursor: 'pointer', marginLeft: '8px', display: 'inline-block' }}
                                  >
                                    <FaYoutube style={{ color: 'red', fontSize: '30px' }} />
                                  </span>
                                )}
                              </div>
                            </strong>
                          </div>
                        </td>
                        <td className="product-detail-column name-column-small-scrn">
                          <div className="product-detail">
                            <strong
                              className="productname mobile-product-name"
                              style={{
                                display: "block",
                                maxWidth: "252px",
                                wordBreak: "break-word",
                                lineHeight: "1.3"
                              }}
                            >
                              {product.product_name}{" "}
                              <span style={{ color: "#d800ab" }}>
                                - {product.qty_per_box || "N/A"}
                              </span>
                            </strong>
                            {isSmallScreen && (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  {parseInt(category.non_discount, 10) === 0 ? (
                                    <>
                                      price:
                                      <i className="bi bi-currency-rupee"></i>
                                      <del style={{ color: "red" }}>
                                        {roundedOldPrice}
                                      </del>
                                      <nobr style={{ color: "green" }}>
                                        <i className="bi bi-currency-rupee"></i>
                                        <b>
                                          {roundedPrice}
                                        </b>
                                      </nobr>
                                    </>
                                  ) : (
                                    <>
                                      price:
                                      <nobr style={{ color: "green" }}>
                                        <i className="bi bi-currency-rupee"></i>
                                        <b>
                                          {roundedOldPrice}
                                        </b>
                                      </nobr>
                                    </>
                                  )}
                                  {product.youtube_url && (
                                    <span
                                      className="youtube-icon-mobile"
                                      onClick={() => handleVideoIconClick(product.youtube_url, product.product_name, product.image)}
                                      style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                                    >
                                      <FaYoutube style={{ color: 'red', fontSize: '33px', marginLeft: "8px" }} />
                                    </span>
                                  )}
                                </div>
                                {storeClose === 0 && (
                                  <div className="counter-btn">
                                    <Button
                                      className="countadd"
                                      onClick={() => handleDecrement(product.product_id)}
                                    >
                                      <b>-</b>
                                    </Button>
                                    <input
                                      type="number"
                                      className="counter-input"
                                      value={counts[product.product_id] === 0 ? '' : counts[product.product_id]}
                                      onChange={(e) => handleCountChange(product.product_id, e.target.value)}
                                      onBlur={(e) => handleCountBlur(product.product_id, e.target.value)}
                                      min="0"
                                      step="1"
                                      placeholder="0"
                                    />
                                    <Button
                                      className="countadd"
                                      onClick={() => handleIncrement(product.product_id, product)}
                                    >
                                      <b>+</b>
                                    </Button>
                                    &nbsp;&nbsp;&nbsp;
                                    {parseInt(category.non_discount, 10) === 0 ? (
                                      <>
                                        <i className="bi bi-currency-rupee"></i>
                                        <b>
                                          {roundToNearestRupee(
                                            counts[product.product_id] *
                                            roundedPrice
                                          )}
                                        </b>
                                      </>
                                    ) : (
                                      <>
                                        Total :
                                        <i className="bi bi-currency-rupee"></i>
                                        {roundToNearestRupee(
                                          counts[product.product_id] *
                                          roundedOldPrice
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </td>

                        {!isSmallScreen && (
                          <>
                            <td className="qty-per-box-column" style={{ color: "#d800ab" }}>
                              {product.qty_per_box || "N/A"}
                            </td>
                            <td className="price-column">
                              {parseInt(category.non_discount, 10) === 0 ? (
                                <>
                                  <i className="bi bi-currency-rupee"></i>
                                  <del style={{ color: "red" }}>
                                    {roundedOldPrice}
                                  </del>
                                  <nobr style={{ color: "green" }}>
                                    <i className="bi bi-currency-rupee"></i>
                                    <b>
                                      {roundedPrice}
                                    </b>
                                  </nobr>
                                </>
                              ) : (
                                <>
                                  <nobr style={{ color: "green" }}>
                                    <i className="bi bi-currency-rupee"></i>
                                    <b>
                                      {roundedOldPrice}
                                    </b>
                                  </nobr>
                                </>
                              )}
                            </td>
                            {storeClose === 0 && (
                              <td className="qty-column">
                                <div className="counter-btn">
                                  <Button
                                    className="countadd"
                                    onClick={() => handleDecrement(product.product_id)}
                                  >
                                    <b>-</b>
                                  </Button>
                                  <input
                                    type="number"
                                    className="counter-input"
                                    value={counts[product.product_id] === 0 ? '' : counts[product.product_id]}
                                    onChange={(e) => handleCountChange(product.product_id, e.target.value)}
                                    onBlur={(e) => handleCountBlur(product.product_id, e.target.value)}
                                    min="0"
                                    step="1"
                                    placeholder="0"
                                  />
                                  <Button
                                    className="countadd"
                                    onClick={() => handleIncrement(product.product_id, product)}
                                  >
                                    <b>+</b>
                                  </Button>
                                </div>
                              </td>
                            )}
                            {storeClose === 0 && (
                              <td className="total-column">
                                {parseInt(category.non_discount, 10) === 0 ? (
                                  <>
                                    <i className="bi bi-currency-rupee"></i>
                                    <b>
                                      {roundToNearestRupee(
                                        counts[product.product_id] *
                                        roundedPrice
                                      )}
                                    </b>
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-currency-rupee"></i>
                                    {roundToNearestRupee(
                                      counts[product.product_id] *
                                      roundedOldPrice
                                    )}
                                  </>
                                )}
                              </td>
                            )}
                          </>
                        )}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={isSmallScreen ? 4 : 7} className="text-center py-4">
                  <div className="no-products-found">
                    <i className="bi bi-search" style={{ fontSize: "2rem", color: "#6c757d", marginBottom: "10px" }}></i>
                    <p className="mb-0">No products found matching "{searchQuery}"</p>
                    <small className="text-muted">Try a different search term</small>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Modal - Kept as is */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="product-modal"
      >
        <Modal.Header closeButton className="modal-header-enhanced">
          <Modal.Title id="contained-modal-title-vcenter">
            {selectedName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-enhanced">
          {selectedVideo ? (
            <div className="video-container">
              <iframe
                src={selectedVideo}
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          ) : (
            <div className="image-container">
              <img
                src={selectedImage}
                alt="Selected"
                className="modal-image"
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-enhanced">
          <Button onClick={() => setShowModal(false)} className="modal-close-btn">
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Scroll to Top */}
      {showButton && (
        <button className="scroll-to-top1" onClick={scrollToTop}>
          <FaArrowUp />
        </button>
      )}

      {/* ===== MOBILE BOTTOM FLOATING BAR - Shows only on mobile ===== */}
      <div className="mobile-bottom-bar">
        <div className="mobile-bottom-info">
          <div className="mobile-bottom-row">
            <span className="label">Discount</span>
            <span className="value discount">-₹{roundToNearestRupee(discountAmount)}</span>
          </div>
          <div className="mobile-bottom-row">
            <span className="label">Total Qty</span>
            <span className="value qty">{qtyCount}</span>
          </div>
          <div className="mobile-bottom-row">
            <span className="label">Total Amount</span>
            <span className="value total">₹{roundToNearestRupee(finalAmount)}</span>
          </div>
        </div>
        <Link to="/cart" className="mobile-bottom-cart">
          <i className="bi bi-cart"></i>
          {cartCount > 0 && <span className="mobile-bottom-badge">{cartCount}</span>}
        </Link>
      </div>

      {/* Mobile Bottom Navigation - Kept as is */}
      <div className="minifooter1">
        <div className="minimenu">
          <Link to="/home">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                color: "white",
              }}
            >
              <i
                className="bi bi-house-door"
                style={{ marginLeft: "12px", fontSize: "1.4rem" }}
              ></i>
              Home
            </div>
          </Link>

          <Link to="/quick">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                color: "white",
              }}
            >
              <i
                className="bi bi-bag"
                style={{ marginLeft: "15px", fontSize: "1.4rem" }}
              ></i>
              Enquiry
            </div>
          </Link>
          {storeClose === 0 ? (
            <Link to="/cart">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  color: "white",
                  position: "relative",
                }}
              >
                <i
                  className="bi bi-cart"
                  style={{
                    marginLeft: "25px",
                    fontSize: "1.4rem",
                    position: "relative",
                  }}
                ></i>
                View Cart
                {cartCount > 0 && (
                  <span
                    className="badge"
                    style={{
                      position: "absolute",
                      top: "-5px",
                      right: "-2px",
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "0.7rem",
                      color: "#a01717",
                    }}
                  >
                    {cartCount}
                  </span>
                )}
              </div>
            </Link>
          ) : (
            <Link to="/about">
              <div style={{ display: "flex", flexDirection: "column", color: "white" }}>
                <i className="bi bi-journal-text officon" style={{ marginLeft: "20px", fontSize: "1.4rem", color: "white" }}></i>
                About Us
              </div>
            </Link>
          )}

          <Link to="/contact">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                color: "white",
              }}
            >
              <i
                className="bi bi-telephone"
                style={{ marginLeft: "12px", fontSize: "1.4rem" }}
              ></i>
              Contact
            </div>
          </Link>
        </div>
      </div>

    </div>
  );
};

export default Quick